import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';

export default function UpdatePassword() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: 'Weak', color: '#ff4444' });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.includes('error=')) {
      const params = new URLSearchParams(hash.substring(1));
      const error = params.get('error');
      const errorDescription = params.get('error_description');
      
      if (error) {
        setMessage(`Authentication Error: ${errorDescription || error}`);
        setMessageType('error');
      }
    }
  }, [location]);

  const checkPasswordStrength = (password) => {
    let score = 0;
    let label = 'Weak';
    let color = '#ff4444';

    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score >= 4) {
      label = 'Strong';
      color = '#00C851';
    } else if (score >= 3) {
      label = 'Good';
      color = '#ffbb33';
    } else if (score >= 2) {
      label = 'Fair';
      color = '#ff8800';
    }

    setPasswordStrength({ score, label, color });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setMessage('');
    setMessageType('');

    if (!oldPassword.trim()) {
      setMessage('Please enter your current password');
      setMessageType('error');
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("New passwords don't match");
      setMessageType('error');
      return;
    }

    if (newPassword.length < 8) {
      setMessage("New password must be at least 8 characters long");
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams(location.hash.substring(1));
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      
      if (!accessToken) {
        throw new Error('No access token found. Please try the password reset again.');
      }

      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      });

      if (error) throw error;

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      setMessage('Password updated successfully! Redirecting to home...');
      setMessageType('success');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      setMessage(error.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  if (messageType === 'error' && message.includes('Authentication Error')) {
    return (
      <div className="auth-container">
        <h2>Password Reset Link Expired</h2>
        <div className="error-message">
          <p>This password reset link has expired or is invalid.</p>
          <p>Password reset links expire after 1 hour for security reasons.</p>
        </div>
        <div className="button-group">
          <button onClick={() => navigate('/')} className="btn-secondary">
            Return to Home
          </button>
          <button 
            onClick={() => {
              setMessage('');
              setMessageType('');
              navigate('/');
            }}
            className="btn-primary"
          >
            Try Password Reset Again
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="auth-container">
      <h2>Reset Your Password</h2>
      <p className="subtitle">Please enter your current password and choose a new one</p>
      
      <form onSubmit={handleSubmit} className="password-form">
        <div className="form-group">
          <label htmlFor="oldPassword">Current Password</label>
          <input
            id="oldPassword"
            type="password"
            placeholder="Enter your current password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="newPassword">New Password</label>
          <input
            id="newPassword"
            type="password"
            placeholder="Enter your new password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              checkPasswordStrength(e.target.value);
            }}
            required
            className="form-input"
          />
          
          <div className="password-strength">
            <div className="strength-bar-container">
              <div 
                className="strength-bar"
                style={{
                  width: `${(passwordStrength.score + 1) * 20}%`,
                  backgroundColor: passwordStrength.color
                }}
              ></div>
            </div>
            <span className="strength-label" style={{ color: passwordStrength.color }}>
              {passwordStrength.label}
            </span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm New Password</label>
          <input
            id="confirmPassword"
            type="password"
            placeholder="Confirm your new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="form-input"
          />
        </div>

        <button type="submit" disabled={loading} className="btn-primary submit-btn">
          {loading ? 'Updating Password...' : 'Confirm Password Change'}
        </button>
      </form>

      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}

      <div className="form-footer">
        <button onClick={() => navigate('/')} className="btn-link">
          Cancel and return to home
        </button>
      </div>
    </div>
  );
}